import { useContext, useEffect, useState } from 'react'
import { Image } from 'components/Image'
import { Button, Typography } from '@achieve/ascend'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import classNames from 'classnames'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'

import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { LayoutContext } from 'providers/LayoutProvider'
import { createExperimentObject } from 'utils/experiments'



const QuizBanner = function ({ showQuizBanner, bannerCopy, onStart, onDecline }) {
  const {
    state: { isPageLoaded },
  } = useContext(AnalyticsContext)
  const [isReady, setIsReady] = useState(false)
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const [menuCondensed] = useLayoutMenuCondensed()

  // for fire and forget events
  const { fireAndForget } = useTriggerEvents()
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const PFQBannerExperiment = useFeatureFlag(
    FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER
  )

  // Add useEffect to handle main-layout padding
  useEffect(() => {
    const mainLayout = document.getElementById('main-layout')
    if (showQuizBanner && mainLayout && isPageLoaded) {
      // Get main-layout original padding top to restore if/when the banner is no longer displayed
      const computedStyle = window.getComputedStyle(mainLayout)
      const originalPadding = computedStyle.paddingTop
      // get just the number
      const paddingValue = parseInt(originalPadding, 10) || 0
      // Add the height of this banner
      const bannerPadding = paddingValue + 56
      // Apply the new padding
      mainLayout.style.paddingTop = bannerPadding + 'px'
      // show banner
      setIsReady(true)

      // Banner Loaded/Displaying event
      fireAndForget({
        event_type: 'app_event',
        event_action: 'app_event',
        nav_link_section: 'PFQ Banner',
        track_event: 'product_fit_quiz_banner_loaded',
        experiments: [
          createExperimentObject(
            'Product Fit Quiz Banner',
            FEATURE_TOGGLES[siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER,
            PFQBannerExperiment,
            'conversion'
          ),
        ],
      })

      // Cleanup function to restore original padding
      return () => {
        mainLayout.style.paddingTop = originalPadding
      }
    }
  }, [showQuizBanner, isMobile, isPageLoaded, fireAndForget, siteEnv, PFQBannerExperiment])

  return (
    <>
      {isReady && (
        <div
          className={classNames(
            'bg-blue-400 shadow-[0_6px_40px_4px_rgb(16_24_40_/_11%),0_9px_12px_3px_rgb(16_24_40_/_5%)] fixed flex max-w-[1440px] -translate-x-2/4 w-full left-2/4 h-[56px]',
            // achieve.com seems to not condense with mobile viewport
            menuCondensed ? 'top-[72px] lg:top-[64px]' : 'top-[72px] lg:top-[96px]'
          )}
          style={{ overflow: 'hidden', zIndex: 1 }}
          data-testid="pfq-banner"
        >
          {/* SVG Background Pattern */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.75,
              zIndex: 1,
            }}
          >
            <Image
              src="/multi-site-assets/achieve-arrow-banner-pattern.svg"
              alt="Background pattern"
              layout="fill"
              priority={false}
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </div>

          {/* Banner Content */}
          <div
            className="flex items-center justify-between lg:justify-start w-full ml-[19px] lg:gap-[20px] lg:pl-[120px]"
            style={{ position: 'relative', zIndex: 2 }}
          >
            <Typography
              variant="labelButtonSm"
              color="#fff"
              component="div"
              data-testid="pfq-text-content"
              className="pr-[6px]"
            >
              {bannerCopy}
            </Typography>

            <div className="flex items-center gap-[12px] pr-[24px]">
              <TrackingWrapper
                track={{
                  list_name: 'Tracking pfq click event',
                  nav_link_section: 'PFQ banner',
                  click_text: 'Yes',
                  event_action: 'button_click',
                }}
                onClick={onStart}
              >
                <Button
                  size="small"
                  color="secondary"
                  variant="contained"
                  data-testid="pfq-yes-button"
                  style={{ width: '52px', height: '32px' }}
                >
                  <Typography component="div" variant="labelButtonSm">
                    Yes
                  </Typography>
                </Button>
              </TrackingWrapper>
              <TrackingWrapper
                track={{
                  list_name: 'Tracking pfq click event',
                  nav_link_section: 'PFQ banner',
                  click_text: 'No',
                  event_action: 'button_click',
                }}
                onClick={onDecline}
              >
                <Button
                  size="small"
                  color="secondary"
                  variant="contained"
                  data-testid="pfq-no-button"
                  style={{ width: '52px', height: '32px' }}
                >
                  <Typography component="div" variant="labelButtonSm">
                    No
                  </Typography>
                </Button>
              </TrackingWrapper>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export { QuizBanner }
